import React from 'react';
import { useCallback, useEffect, useState } from 'react';
import {
  PartnerContactInformationListRequest,
  useDeletePartnerContactInformationMutation,
  useFetchPartnerContactInformationsQuery,
} from '../partnercontactinformation-api';
import AuditLogsScreen from '../../../components/common/auditLogs';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { PageHeader } from '@ant-design/pro-layout';
import {
  Alert,
  Button,
  Card,
  message,
  Modal,
  Space,
  Table,
  Menu,
  Dropdown
} from 'antd';
import { EditOutlined, DeleteOutlined, ExclamationCircleOutlined, MoreOutlined, InfoCircleOutlined } from '@ant-design/icons';
import {
  FilterValue,
  SorterResult,
  TableCurrentDataSource,
  TablePaginationConfig,
} from 'antd/lib/table/interface';
import { PartnerContactInformationViewModel, PaginatedResult, PartnerContactInformationListViewModel } from '../../api/models';
import { DefaultPage, DefaultPageSize } from '../../../core/defaults';
import Title from 'antd/lib/typography/Title';
import { getLink } from '../../../App-router';
import { TableState } from '../../../components/table/model';
import getSortBy from '../../../components/table/sorter';

const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

export default function PartnerContactInformationsScreen(props: any) {
  const query = useQuery();
  const params: any = useParams<any>();
  const navigate = useNavigate();
  const [showAuditLogs, setShowAuditLogs] = useState<boolean>(false);
  const [entityId, setEntityId] = useState<number>(0);
  // console.log('PartnerContactInformationsScreen', props);
  const partnerContactInformationTable: TableState = {
    columns: [
      {
        title: 'Id',
        dataIndex: '',
        key: 'Id',
        render: (partnerContactInformation: PartnerContactInformationListViewModel) => (
          <>
              <Space>
                <span>{partnerContactInformation.id}</span>
              </Space>
          </>
        ),
        sorter: false,
        fixed: 'left',
      },
      {
        title: 'Contact Type',
        dataIndex: '',
        key: 'contactType',
        render: (partnerContactInformation: PartnerContactInformationListViewModel) => (
          <>
              <span>{partnerContactInformation.contactType}</span>
          </>
        ),
      },
      {
        title: 'Name',
        dataIndex: '',
        key: 'name',
        render: (partnerContactInformation: PartnerContactInformationListViewModel) => (
          <>
              <span>{partnerContactInformation.name}</span>
          </>
        ),
      },      
      {
        title: 'Email',
        dataIndex: '',
        key: 'email',
        render: (partnerContactInformation: PartnerContactInformationListViewModel) => (
          <>
              <span>{partnerContactInformation.email}</span>
          </>
        ),
      },
      {
        title: 'Phone',
        dataIndex: '',
        key: 'phone',
        render: (partnerContactInformation: PartnerContactInformationListViewModel) => (
          <>
              <span>{partnerContactInformation.phone}</span>
          </>
        ),
      },
      {
        title: 'Note',
        dataIndex: '',
        key: 'note',
        render: (partnerContactInformation: PartnerContactInformationListViewModel) => (
          <>
              <span>{partnerContactInformation.note}</span>
          </>
        ),
      },
      {
        title: 'Primary',
        dataIndex: '',
        key: 'primary',
        render: (partnerContactInformation: PartnerContactInformationListViewModel) => (
          <>
              <span>{partnerContactInformation.primary?'Yes':''}</span>
          </>
        ),
      },
      {
        title: 'Actions',
        key: 'action',
        dataIndex: '',
        fixed:'right',
        render: (partnerContactInformation: PartnerContactInformationListViewModel) => (
          <Dropdown
            trigger={['click']}
            placement="bottomRight"
            overlay={
              <Menu style={{ padding: '8px 8px' }}>
                <Menu.Item icon={<EditOutlined />} key="1">
                  <Link
                    to={getLink('PartnerContactInformationEdit', {
                      tenantId: params.tenantId,
                      Id: partnerContactInformation.id,
                    })}
                  >
                    Edit
                  </Link>
                </Menu.Item>
                <Menu.Item icon={<InfoCircleOutlined />} key="3">
                <Link to="#" onClick={()=>setShowAuditLogs(true)}>
                    Audit Logs
                  </Link>
                </Menu.Item>
                <Menu.Item
                  icon={<DeleteOutlined />}
                  key={partnerContactInformation.id}
                  disabled={deletingId === partnerContactInformation.id}
                  onClick={() => {
                    Modal.confirm({
                      title: (
                        <span>
                          Delete partner contact <b>{partnerContactInformation.name || partnerContactInformation.id}</b>?
                        </span>
                      ),
                      icon: <ExclamationCircleOutlined />,
                      onOk() {
                        handlePartnerContactInformationDelete(partnerContactInformation);
                      },
                    });
                  }}
                >
                  Delete
                </Menu.Item>
              </Menu>
            }
          >
            <MoreOutlined />
          </Dropdown>
        ),
      },
    ],
  };

  const getCurrentState = (): PartnerContactInformationListRequest => ({
    current: Number(query.get('p')) || DefaultPage,
    pageSize: Number(query.get('size')) || DefaultPageSize,
    filter:'',
    sortBy: query.get('sortBy') || 'EnteredByDate Desc',
    partnerInformationId: props.partnershipId
  });

  const [pagination, setPagination] = useState<PartnerContactInformationListRequest>(getCurrentState());

  const {
    data: pagedData = {},
    isFetching,
    error,
  }: {
    data?: PaginatedResult<PartnerContactInformationListViewModel> | undefined;
    isFetching?: boolean | undefined;
    error?: any;
  } = useFetchPartnerContactInformationsQuery(pagination);

  useEffect(() => {
    localStorage.setItem('partnerInformationId', props.partnershipId)
    setPagination(getCurrentState());
  }, [params,props]);

  const [deletePartnerContactInformation, { isLoading: isDeleteLoading, error: deleteError }] =
    useDeletePartnerContactInformationMutation();

  const onCreateNew = useCallback(
    () => navigate(getLink('PartnerContactInformationEdit', { ...params, Id: 'new' })),
    [params, navigate]
  );

  const handleTableChange = (
    paginationConfig: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<PartnerContactInformationViewModel> | SorterResult<PartnerContactInformationViewModel>[],
    extra: TableCurrentDataSource<PartnerContactInformationViewModel>
  ) => {
    // Handle sorter - it can be null, undefined, a single object, or an array
    const sorterResult = Array.isArray(sorter) ? sorter[0] : sorter;
    const { columnKey, order } = (sorterResult || {}) as SorterResult<PartnerContactInformationViewModel>;
    if (columnKey && order) {
      query.set('sortBy', getSortBy(columnKey.toString(), order));
    } else {
      query.set('sortBy', '');
    }

    navigate(
      getLink(
        'PartnershipInformationEdit',
        { ...params },
        {
          ...query,
          sortBy: query.get('sortBy'),
          p: paginationConfig.current,
          size: paginationConfig.pageSize,
          q: pagination.filter || '',
        }
      )
    );
  };


  const [deletingId, setDeletingId] = useState<number | null>(null);

  // antd's message API and Alert render their argument as a React node, so anything
  // that is not a string (e.g. a ProblemDetails body) would throw and trip the global
  // ErrorBoundary instead of showing an error.
  const toDisplayMessage = (value: any, fallback: string): string => {
    if (typeof value === 'string' && value.trim()) return value;
    if (value === null || value === undefined) return fallback;
    if (typeof value === 'object') {
      const candidate = value.message ?? value.detail ?? value.title ?? value.error;
      if (typeof candidate === 'string' && candidate.trim()) return candidate;
      return fallback;
    }
    return String(value);
  };

  const handlePartnerContactInformationDelete = async (partnercontactinformation: PartnerContactInformationListViewModel) => {
    const idToDelete = partnercontactinformation.id ?? 0;
    try {
      setDeletingId(idToDelete);
      const response = await deletePartnerContactInformation({
        Id: idToDelete,
        updatedBy: sessionStorage.getItem('userEmail') || '',
      }).unwrap();

      if (response?.status?.toLowerCase() === 'success') {
        message.success(
          toDisplayMessage(response?.message, `Partner contact ${partnercontactinformation.name || idToDelete} deleted`)
        );
      } else {
        message.error(toDisplayMessage(response?.message, 'Failed to delete partner contact'));
      }
    } catch (error: any) {
      console.error('Delete partner contact failed', error);
      message.error(toDisplayMessage(error?.data ?? error, 'Failed to delete partner contact'));
    } finally {
      setDeletingId(null);
    }
  };
  
  return (
    <div>
      <PageHeader
        extra={
          <Button htmlType="button" data-testid={'create-new-partnercontactinformation'} onClick={onCreateNew}>
            New Partner Contact Information
          </Button>
        }
        title={
          <Title
            level={4}
            style={{
              marginBottom: 0,
              display: 'inline-block',
            }}
          >
            Partner Contact Information
          </Title>
        }
      >
        <Card>
          {error?.status && (
            <Alert message="Error" description={error.data} type="error" showIcon />
          )}
          {deleteError && (
            <Alert
              message="Error"
              description={toDisplayMessage((deleteError as any)?.data ?? deleteError, 'Failed to delete partner contact')}
              type="error"
              showIcon
            />
          )}
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <Table scroll={{ x: true }}
              dataSource={pagedData.results}
              onChange={handleTableChange}
              rowKey={'id'}
              pagination={{
                ...pagination,
                total: pagedData.totalCount,
                showTotal: (total) => `Total ${total} items`,
                pageSizeOptions: ["10", "20", "50","100"],
                showSizeChanger: true,
                locale: { items_per_page: " / Page" }
              }}
              columns={partnerContactInformationTable.columns}
              loading={isFetching}
              onRow={(record, rowIndex) => {
                return {
                  onClick: event => { 
                    setEntityId(record?.id||0);
                  },
                };
              }}
            />
          </Space>
        </Card>        
        {showAuditLogs?<AuditLogsScreen entityId={entityId} entityName={'PartnerContactInformation'} showAuditLogs={setShowAuditLogs}/>:<></>}
      </PageHeader>
    </div>
  );
}
